// data/calendarEventColors.js

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

/**
 * Toutes les couleurs disponibles dans le calendrier.
 *
 * Tu peux modifier librement les valeurs hexadécimales.
 *
 * Chaque événement peut également recevoir directement une clé :
 *
 * event.calendar_color = "competition"
 * event.calendar_color = "stage"
 * event.calendar_color = "formation"
 * etc.
 */
export const CALENDAR_EVENT_PALETTES = {
  competition: {
    key: "competition",
    label: "Compétitions",
    accent: "#dc2626",

    lightBackground: "#fef2f2",
    lightBorder: "#fecaca",
    lightText: "#991b1b",

    darkBackground: "#450a0a",
    darkBorder: "#991b1b",
    darkText: "#fecaca",
  },

  stage: {
    key: "stage",
    label: "Stages et entraînements",
    accent: "#2563eb",

    lightBackground: "#eff6ff",
    lightBorder: "#bfdbfe",
    lightText: "#1e40af",

    darkBackground: "#172554",
    darkBorder: "#1e40af",
    darkText: "#bfdbfe",
  },

  formation: {
    key: "formation",
    label: "Formations",
    accent: "#7c3aed",

    lightBackground: "#f5f3ff",
    lightBorder: "#ddd6fe",
    lightText: "#5b21b6",

    darkBackground: "#2e1065",
    darkBorder: "#6d28d9",
    darkText: "#ddd6fe",
  },

  grade: {
    key: "grade",
    label: "Grades et examens",
    accent: "#d97706",

    lightBackground: "#fffbeb",
    lightBorder: "#fde68a",
    lightText: "#92400e",

    darkBackground: "#451a03",
    darkBorder: "#92400e",
    darkText: "#fde68a",
  },

  animation: {
    key: "animation",
    label: "Animations",
    accent: "#059669",

    lightBackground: "#ecfdf5",
    lightBorder: "#a7f3d0",
    lightText: "#065f46",

    darkBackground: "#022c22",
    darkBorder: "#047857",
    darkText: "#a7f3d0",
  },

  meeting: {
    key: "meeting",
    label: "Réunions",
    accent: "#0891b2",

    lightBackground: "#ecfeff",
    lightBorder: "#a5f3fc",
    lightText: "#155e75",

    darkBackground: "#083344",
    darkBorder: "#0e7490",
    darkText: "#a5f3fc",
  },

  default: {
    key: "default",
    label: "Autres événements",
    accent: "#64748b",

    lightBackground: "#f8fafc",
    lightBorder: "#cbd5e1",
    lightText: "#334155",

    darkBackground: "#1e293b",
    darkBorder: "#475569",
    darkText: "#e2e8f0",
  },
};

/**
 * Correspondances exactes avec type_event.
 *
 * Les clés doivent être écrites en minuscules et sans accents.
 */
export const CALENDAR_EXACT_TYPE_COLORS = {
  competition: "competition",
  "competition officielle": "competition",
  championnat: "competition",
  tournoi: "competition",
  coupe: "competition",

  stage: "stage",
  entrainement: "stage",
  regroupement: "stage",

  formation: "formation",

  examen: "grade",
  grade: "grade",
  shiai: "grade",

  animation: "animation",
  interclubs: "animation",

  reunion: "meeting",
  commission: "meeting",
};

/**
 * Règles de secours.
 *
 * Elles sont appliquées lorsque type_event ne correspond pas exactement
 * à une valeur définie dans CALENDAR_EXACT_TYPE_COLORS.
 *
 * Exemple pour colorer certains niveaux :
 *
 * {
 *   palette: "competition",
 *   fields: ["level_event"],
 *   includes: ["national", "international"],
 * }
 */
export const CALENDAR_COLOR_RULES = [
  {
    palette: "competition",
    fields: ["type_event", "titre"],
    includes: [
      "competition",
      "championnat",
      "tournoi",
      "coupe",
      "criterium",
      "open",
    ],
  },

  {
    palette: "stage",
    fields: ["type_event", "titre"],
    includes: [
      "stage",
      "entrainement",
      "regroupement",
      "selection",
      "preparation",
    ],
  },

  {
    palette: "formation",
    fields: ["type_event", "titre"],
    includes: [
      "formation",
      "ecole des cadres",
      "enseignant",
      "arbitre",
      "commissaire sportif",
    ],
  },

  {
    palette: "grade",
    fields: ["type_event", "titre"],
    includes: [
      "examen",
      "passage de grade",
      "grade",
      "kata",
      "shiai",
      "uv1",
      "uv2",
      "uv3",
      "uv4",
    ],
  },

  {
    palette: "animation",
    fields: ["type_event", "titre"],
    includes: [
      "animation",
      "interclubs",
      "judo famille",
      "portes ouvertes",
    ],
  },

  {
    palette: "meeting",
    fields: ["type_event", "titre"],
    includes: [
      "reunion",
      "assemblee",
      "commission",
      "comite directeur",
      "conference",
    ],
  },
];

export function getEventPaletteKey(event = {}) {
  /*
   * Priorité 1 :
   * couleur définie directement sur l'événement.
   *
   * Ces champs ne sont pas encore obligatoires dans ton back.
   * Ils rendent simplement le front compatible avec une future évolution.
   */
  const explicitColor = normalize(
    event.calendar_color ||
      event.color_event ||
      event.couleur_event ||
      ""
  );

  if (explicitColor) {
    const matchingKey = Object.keys(CALENDAR_EVENT_PALETTES).find(
      (key) => normalize(key) === explicitColor
    );

    if (matchingKey) {
      return matchingKey;
    }
  }

  /*
   * Priorité 2 :
   * correspondance exacte sur type_event.
   */
  const normalizedType = normalize(event.type_event);

  if (
    normalizedType &&
    CALENDAR_EXACT_TYPE_COLORS[normalizedType] &&
    CALENDAR_EVENT_PALETTES[
      CALENDAR_EXACT_TYPE_COLORS[normalizedType]
    ]
  ) {
    return CALENDAR_EXACT_TYPE_COLORS[normalizedType];
  }

  /*
   * Priorité 3 :
   * règles basées sur des mots-clés.
   */
  for (const rule of CALENDAR_COLOR_RULES) {
    const searchableText = rule.fields
      .map((field) => normalize(event?.[field]))
      .join(" ");

    const matches = rule.includes.some((keyword) =>
      searchableText.includes(normalize(keyword))
    );

    if (matches && CALENDAR_EVENT_PALETTES[rule.palette]) {
      return rule.palette;
    }
  }

  return "default";
}

export function getEventPalette(event = {}) {
  const key = getEventPaletteKey(event);

  return (
    CALENDAR_EVENT_PALETTES[key] ||
    CALENDAR_EVENT_PALETTES.default
  );
}

export function getCalendarLegendEntries(events = []) {
  const usedKeys = new Set(
    events.map((event) => getEventPaletteKey(event))
  );

  return Object.values(CALENDAR_EVENT_PALETTES).filter((palette) =>
    usedKeys.has(palette.key)
  );
}